/**
 * "View on Phone" — hand-off from the web portal to the Voye mobile app.
 *
 * The two front-ends share one merged backend, so the same account works in
 * both. This gets the customer onto their phone at the screen they were
 * already on.
 *
 * ## Why a QR code and not a signed link
 *
 * The obvious "true one-tap" is to put a session token in the URL so the phone
 * opens already logged in. That URL then lives in browser history, server
 * access logs and referrer headers — a credential leaking into three places
 * that are routinely retained and shared. A QR carries only a public URL: the
 * customer signs in once on the phone, and the app remembers them afterwards,
 * so the cost is paid once rather than the risk being carried forever.
 *
 * ## What the click does
 *
 * It OPENS the mobile app in a new tab, every time. The QR panel used to be the
 * whole interaction on a desktop, which meant a click produced a code to scan
 * rather than the thing the button's label promises. The panel is still there
 * underneath for the genuine "carry on over on my phone" case — that is what
 * the QR is for — but the app opens first, because that is what was asked for.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { Check, Copy, ExternalLink, Smartphone } from 'lucide-react';
import { mobileAppUrl } from '../../lib/featureFlags';

/**
 * Map a portal route onto the mobile app's equivalent.
 *
 * The two apps have different route shapes, so continuity needs an explicit
 * mapping rather than passing the path through. Anything unmapped goes to the
 * mobile home screen — a wrong deep link is worse than a predictable one.
 */
function mobilePathFor(portalPath: string): string {
  if (portalPath.startsWith('/customer/airtime')) return '/voye/airtime';
  if (portalPath.startsWith('/customer/requests')) return '/voye/activity';
  if (portalPath.startsWith('/customer/settings')) return '/voye/account';
  return '/voye';
}

function isPhone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches;
}

export function ViewOnPhoneButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const base = mobileAppUrl();
  // Deep-link to the equivalent screen, so a customer mid-flow lands where
  // they were rather than at the home screen.
  const url = base ? `${base.replace(/\/voye$/, '')}${mobilePathFor(location.pathname)}` : '';

  useEffect(() => {
    if (!open || !url) return;
    QRCode.toString(url, { type: 'svg', margin: 1, width: 180 })
      .then(setQr)
      .catch(() => setQr(null));
  }, [open, url]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is unavailable over plain HTTP in some browsers. The URL is
      // visible and selectable in the panel, so this is a graceful no-op.
    }
  };

  // No configured mobile URL means no working destination — hide rather than
  // offer a dead link.
  if (!url) return null;

  function onClick() {
    // Open the app — that is what the button says it does.
    window.open(url, '_blank', 'noopener');

    // On a desktop also reveal the QR, so someone who meant "continue on my
    // actual phone" has it without a second click. On a phone the tab that
    // just opened IS the app, so a code to scan would be noise.
    if (!isPhone()) setOpen(true);
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={onClick}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Continue on your phone"
        className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-[12.5px] font-semibold text-[var(--color-secondary)] hover:bg-[var(--color-panel)] hover:text-[var(--color-ink)]"
      >
        <Smartphone className="w-4 h-4" />
        <span className="hidden sm:inline">View on Phone</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Continue on your phone"
          className="absolute right-0 mt-2 w-64 rounded-xl border border-[var(--color-border)] bg-white p-4 shadow-lg z-40"
        >
          <p className="text-[12.5px] font-semibold text-[var(--color-ink)]">Opening on this computer</p>
          <p className="mt-1 text-[11.5px] leading-relaxed text-[var(--color-secondary-2)]">
            To carry on from your phone instead, point its camera here. You'll sign in
            once, then stay signed in.
          </p>

          {qr && (
            <div
              className="mt-3 flex justify-center rounded-lg bg-white p-2 [&>svg]:h-40 [&>svg]:w-40"
              // The SVG comes from the qrcode library encoding our OWN url —
              // no user input reaches it.
              dangerouslySetInnerHTML={{ __html: qr }}
            />
          )}

          <div className="mt-3 rounded-md bg-[var(--color-panel)] border border-[var(--color-border)] px-2.5 py-2">
            <code className="block break-all text-[11px] text-[var(--color-body)]">{url}</code>
          </div>

          <div className="mt-2.5 flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-2.5 py-2 text-[12px] font-semibold text-white hover:bg-brand-700"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open again
            </a>
            <button
              type="button"
              onClick={copy}
              aria-label="Copy link"
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-2 text-[12px] font-semibold text-[var(--color-body)] hover:bg-[var(--color-panel)]"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
